import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { helpRoleName, paletteBadge, paletteSearch, pathForView, type PaletteGoTo } from '@aithora/shared';
import { useConsoleRole } from '../session/SessionContext';
import { CONSOLE_INDEX } from '../content/consoleIndex';
import { provenanceChip, sx } from '../ui/designStyles';

/**
 * The command palette (Cmd or Ctrl+K), ported from the design (T116-170, paletteVals
 * L28743). "Go to" jumps to a page the role may open; "What is" reads a definition from
 * the console's help text in place, without leaving the page. It searches the help text
 * only: the design's palette never searches records, so neither does this.
 */

/** The tutorial written for each role (design cpTutorial*, L28862), opened from Help. */
const TUTORIAL: Record<string, string> = {
  'Facility Admin': 'Setting up your building',
  'Care Staff': 'Your shift, in five screens',
  Resident: 'Getting started',
  Family: 'What you can see',
  Operator: 'How we talk about our own numbers',
  'Aithora Home': 'Getting started at home',
};

const HINT = 'Type a page name to jump, or a term like MODEL, occupancy or eligibility to see what it means. Arrow keys and Enter to pick, Escape to close.';
const NO_HITS = 'Nothing matches that. Try a page name, a badge, or a word from the thing you are looking at.';

const S = {
  scrim: 'position:fixed;inset:0;z-index:400;background:rgba(34,39,46,.34);display:flex;align-items:flex-start;justify-content:center;padding:80px 20px 20px;',
  box: 'box-sizing:border-box;width:100%;max-width:620px;background:#FCFAF4;border:1px solid rgba(34,39,46,.14);border-radius:12px;box-shadow:0 20px 50px rgba(34,39,46,.24);overflow:hidden;',
  head: 'display:flex;align-items:center;gap:10px;padding:13px 16px;border-bottom:1px solid rgba(34,39,46,.12);',
  input: "box-sizing:border-box;flex:1 1 auto;min-width:0;height:30px;padding:0;border:0;background:transparent;font-family:'Archivo',system-ui,sans-serif;font-size:15px;color:#22272E;outline:none;",
  close: 'font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.08em;color:#54606C;text-decoration:none;white-space:nowrap;flex:0 0 auto;',
  guideRow: 'display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;padding:9px 16px;border-bottom:1px solid rgba(34,39,46,.1);background:rgba(79,138,214,.05);',
  tutorialLink: 'display:inline-flex;align-items:center;gap:6px;font-size:14px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;',
  guideLink: 'display:inline-flex;align-items:center;gap:6px;font-size:14px;color:#2F5C97;text-decoration:none;white-space:nowrap;',
  meta: 'font-size:14px;color:#54606C;white-space:nowrap;',
  rule: 'width:1px;height:13px;background:rgba(34,39,46,.18);flex:0 0 1px;',
  list: 'max-height:60vh;overflow-y:auto;',
  note: 'padding:16px;font-size:13px;color:#54606C;line-height:1.6;text-wrap:pretty;',
  groupHead: 'padding:11px 16px 5px;font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.13em;text-transform:uppercase;color:#54606C;font-weight:700;',
  whatHead: 'padding:12px 16px 5px;font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.13em;text-transform:uppercase;color:#54606C;font-weight:700;border-top:1px solid rgba(34,39,46,.1);',
  row: (on: boolean) => 'display:flex;align-items:center;gap:10px;padding:9px 16px;text-decoration:none;color:#22272E;border-top:1px solid rgba(34,39,46,.06);' + (on ? 'background:rgba(79,138,214,.12);' : ''),
  rowLabel: 'flex:1 1 auto;min-width:0;font-size:14px;font-weight:600;',
  rowSub: 'font-size:14px;color:#54606C;white-space:nowrap;flex:0 0 auto;',
  what: 'padding:10px 16px;border-top:1px solid rgba(34,39,46,.06);',
  whatTop: 'display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:5px;',
  whatLabel: 'font-size:13.5px;font-weight:700;',
  grow: 'flex:1 1 40px;min-width:20px;',
  whatBody: 'font-size:13px;color:#3C454F;line-height:1.55;margin-top:4px;text-wrap:pretty;',
};

const chipFor = (k: string) => {
  const [fg, bg] = paletteBadge(k);
  return provenanceChip(fg, bg);
};

export function CommandPalette({ onClose }: { onClose: () => void }) {
  const { role } = useConsoleRole();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [idx, setIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const { goTo, whatIs } = useMemo(() => paletteSearch(CONSOLE_INDEX, query, role), [query, role]);
  const tutorial = TUTORIAL[helpRoleName(role)] ?? null;
  const q = query.trim();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  useEffect(() => setIdx(0), [query]);

  const pick = (r: PaletteGoTo | undefined) => {
    if (!r) return;
    onClose();
    navigate(r.tab ? `${pathForView(r.view)}?tab=${encodeURIComponent(r.tab)}` : pathForView(r.view));
  };

  const onKey = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIdx((i) => Math.min(i + 1, Math.max(0, goTo.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(goTo[idx]);
    }
  };

  return (
    <div style={sx(S.scrim)} onMouseDown={(e) => e.target === e.currentTarget && onClose()} onKeyDown={onKey}>
      <div role="dialog" aria-modal="true" aria-label="Jump to a page" style={sx(S.box)}>
        <div style={sx(S.head)}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#54606C" strokeWidth="2" style={sx('flex:0 0 15px;')} aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M16.5 16.5 21 21" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Jump to a page, or ask what something means"
            aria-label="Jump to a page, or ask what something means"
            role="combobox"
            aria-expanded={goTo.length > 0}
            aria-controls="cp-list"
            aria-activedescendant={goTo[idx] ? `cp-go-${idx}` : undefined}
            style={sx(S.input)}
          />
          <button type="button" className="dlink" style={sx(S.close)} onClick={onClose}>
            CLOSE
          </button>
        </div>

        <div style={sx(S.guideRow)}>
          {tutorial && (
            <>
              <Link to="/help" style={sx(S.tutorialLink)} onClick={onClose}>
                <span>{tutorial}</span>
                {' →'}
              </Link>
              <span style={sx(S.meta)}>written for your role</span>
              <span style={sx(S.rule)} />
            </>
          )}
          <Link to="/help" style={sx(S.guideLink)} onClick={onClose}>
            <span>Open the full console guide</span>
            {' →'}
          </Link>
          <span style={sx('flex:1 1 60px;min-width:20px;')} />
          <span style={sx(S.meta)}>one entry per page, scoped to your role</span>
        </div>

        <div id="cp-list" style={sx(S.list)}>
          {q.length === 0 && <div style={sx(S.note)}>{HINT}</div>}
          {q.length > 0 && goTo.length === 0 && whatIs.length === 0 && <div style={sx(S.note)}>{NO_HITS}</div>}
          {goTo.length > 0 && (
            <>
              <div style={sx(S.groupHead)}>Go to</div>
              <div role="listbox" aria-label="Go to">
                {goTo.map((r, i) => (
                  <button
                    key={`${r.label}-${r.view}`}
                    id={`cp-go-${i}`}
                    type="button"
                    role="option"
                    aria-selected={i === idx}
                    className="dlink cp-row"
                    style={{ ...sx(S.row(i === idx)), width: '100%' }}
                    onMouseEnter={() => setIdx(i)}
                    onClick={() => pick(r)}
                  >
                    <span style={sx(S.rowLabel)}>{r.label}</span>
                    <span style={sx(S.rowSub)}>{r.sub}</span>
                    <span style={sx(chipFor(r.badge))}>{r.badge}</span>
                  </button>
                ))}
              </div>
            </>
          )}
          {whatIs.length > 0 && (
            <>
              <div style={sx(S.whatHead)}>What is</div>
              {whatIs.map((w, i) => (
                <div key={`${w.label}-${i}`} style={sx(S.what)}>
                  <div style={sx(S.whatTop)}>
                    <span style={sx(S.whatLabel)}>{w.label}</span>
                    <span style={sx(chipFor(w.badge))}>{w.badge}</span>
                    <span style={sx(S.grow)} />
                    <span style={sx(S.meta)}>{w.from}</span>
                  </div>
                  <div style={sx(S.whatBody)}>{w.body}</div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
